import { ITableColumn } from '@models/Forms/IForms';
import { getCellState, ICellRenderDeps, ICellRules } from './tableCells';
import { ITableColumnSource, TableRow } from './tableColumns';

/** Столбец, скрытый набором колонок, в котором есть невалидные ячейки. */
export interface IHiddenIssueColumn {
    /** id колонки грида — им же включается видимость. */
    columnId: string;
    title: string;
}

export interface IHiddenColumnIssues {
    columns: IHiddenIssueColumn[];
    /** Число строк хотя бы с одной такой ячейкой. */
    rowCount: number;
    /** Обход прервал лимит: строк на самом деле больше. */
    truncated: boolean;
    /** Все найденные ячейки пусты — сообщение говорит «не заполнены»; иначе
     *  среди них есть нарушения формата или длины, и это «проверьте». */
    onlyEmpty: boolean;
}

/** Дальше считать нечего: сообщение всё равно говорит «более N строк», а на
 *  таблице в тысячи строк каждая лишняя строка — прогон правил колонки. */
export const HIDDEN_ISSUES_ROW_LIMIT = 500;

/** Правило-формула против константы (одно в одно с useColumnRules). */
const isFormulaRule = (rule: string | undefined): rule is string => !!rule && rule !== 'true' && rule !== 'false';

/** Заголовок — тот же, что стоит в шапке грида (`buildTableColumns`). */
const titleOf = (source: ITableColumnSource): string => {
    const column = source.column as ITableColumn;
    return column.name || column.key || source.columnId;
};

/**
 * Ошибки в столбцах, спрятанных текущим набором колонок.
 *
 * Валидация документа (`validateDocumentTable`) про наборы не знает и проверяет
 * такие столбцы наравне с видимыми: пустое обязательное поле в скрытом столбце
 * блокирует сохранение, а на экране нет ни одной красной ячейки. Здесь считается
 * ровно то, что показывает сигнал в туллбаре.
 *
 * Состояние ячейки берётся из `getCellState` — того же, что рисует подсветку и
 * корешок строки: иначе цифры сообщения разойдутся с тем, что видно после
 * показа столбцов.
 *
 * @param sources колонки-кандидаты: уже отфильтрованы по «скрыта набором» и
 *  «не скрыта формулой видимости» (последние не проверяет и валидация).
 */
export const collectHiddenColumnIssues = async (
    sources: ITableColumnSource[],
    rows: TableRow[],
    evaluate: (rule: string, row: TableRow) => Promise<unknown>,
    deps: ICellRenderDeps,
    rowLimit: number = HIDDEN_ISSUES_ROW_LIMIT,
): Promise<IHiddenColumnIssues> => {
    const columns: IHiddenIssueColumn[] = [];
    const rowsWithIssue = new Set<number>();
    let truncated = false;
    let onlyEmpty = true;

    // Колонок-кандидатов обычно единицы, строк — тысячи: внешний цикл по
    // колонкам даёт выйти из обхода строк сразу после лимита.
    for (const source of sources) {
        const column = source.column as ITableColumn;
        // Обязательность-формула считается здесь, а не берётся из прекэша правил:
        // прекэш наполняется по мере ПОКАЗА строк, а спрятанный столбец по
        // определению не показан ни в одной из них.
        const requiredRule = isFormulaRule(column.required) ? column.required : undefined;
        // Формульный readonly не вычисляем — как и корешок строки (`rowStatus`):
        // на обязательность он не влияет, а лишний прогон формул стоит дорого.
        const readOnly = column.readonly === 'true';

        let hit = false;
        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            const rules: ICellRules | undefined = requiredRule
                ? { required: !!(await evaluate(requiredRule, row)) }
                : undefined;
            if (getCellState(source.column, row, deps, rules, readOnly) !== 'invalid') continue;
            hit = true;
            const value = row[column.key];
            if (!(value === undefined || value === null || String(value).trim() === '')) onlyEmpty = false;
            rowsWithIssue.add(i);
            if (rowsWithIssue.size >= rowLimit) {
                truncated = true;
                break;
            }
        }
        if (hit) columns.push({ columnId: source.columnId, title: titleOf(source) });
        if (truncated) break;
    }

    return { columns, rowCount: rowsWithIssue.size, truncated, onlyEmpty };
};
